/**
 * MasterHomepageSections — ICRAFTZ 2.0 Master Design System.
 *
 * Faithful translation of the approved 11-page master mockup.
 *
 *  1. <PathOfTransformation />   — 5 LARGE stone tablets w/ embossed brass icons
 *     Discover (Jigyasa) · Learn (Bodha) · Commit (Sankalpa) · Practise (Sadhana) · Transform (Siddhi)
 *
 *  2. <PopularCollections />     — 6 TALL parchment manuscript cards w/ brass medallions
 *     Habits · Focus & Deep Work · Wealth & Money · Mindset & Growth · Leadership · Purpose & Life
 *
 *  3. <TransformationRibbon />   — Om | "Transformation is not a day's work..." | Lotus
 */
import React from "react";
import { motion } from "motion/react";
import "../styles/Parchment.css";
import "../styles/MasterHomepage.css";

const ASSET = (slug) => `${process.env.PUBLIC_URL || ""}/illustrations/${slug}.png`;

const TABLETS = [
  { en: "Discover",  sa: "जिज्ञासा", roman: "Jigyasa",  icon: "tablet-icon-compass" },
  { en: "Learn",     sa: "बोध",      roman: "Bodha",    icon: "tablet-icon-book" },
  { en: "Commit",    sa: "संकल्प",   roman: "Sankalpa", icon: "tablet-icon-flame" },
  { en: "Practise",  sa: "साधना",    roman: "Sadhana",  icon: "tablet-icon-meditator" },
  { en: "Transform", sa: "सिद्धि",   roman: "Siddhi",   icon: "tablet-icon-lotus" },
];

const COLLECTIONS = [
  { slug: "habits",     title: "Habits",            desc: "Build better habits that last.",      medallion: "medallion-habits" },
  { slug: "focus",      title: "Focus & Deep Work", desc: "Eliminate distractions.\nDo what matters.", medallion: "medallion-focus" },
  { slug: "wealth",     title: "Wealth & Money",    desc: "Grow wisely.\nLive abundantly.",      medallion: "medallion-wealth" },
  { slug: "mindset",    title: "Mindset & Growth",  desc: "Think better.\nLive better.",         medallion: "medallion-mindset" },
  { slug: "leadership", title: "Leadership",        desc: "Lead yourself.\nThen others.",        medallion: "medallion-leadership" },
  { slug: "purpose",    title: "Purpose & Life",    desc: "Live with clarity\nand meaning.",     medallion: "medallion-purpose" },
];

export function PathOfTransformation() {
  return (
    <section className="mh-path" id="transformation-journey" data-testid="mh-path-of-transformation">
      <header className="mh-section-head">
        <div className="mh-ornament-divider">
          <span className="mh-ornament-rule" />
          <span className="mh-ornament-title">HOW IT WORKS — The Path of Transformation</span>
          <span className="mh-ornament-rule" />
        </div>
      </header>

      <ol className="mh-tablets" data-testid="mh-tablets">
        {TABLETS.map((s, i) => (
          <motion.li
            key={s.roman}
            className="mh-tablet"
            initial={{ opacity: 0, y: 26 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.6, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] }}
            data-testid={`mh-tablet-${s.roman.toLowerCase()}`}
          >
            <div
              className="mh-tablet-stone"
              style={{ backgroundImage: `url('${ASSET("tablet-blank")}')` }}
            >
              <span className="mh-tablet-emblem-wrap">
                <img
                  src={ASSET(s.icon)}
                  alt=""
                  aria-hidden="true"
                  className="mh-tablet-emblem"
                  loading="lazy"
                />
              </span>
              <span className="mh-tablet-en">{s.en}</span>
              <span className="mh-tablet-sa indic">
                {s.sa} <em>({s.roman})</em>
              </span>
            </div>
          </motion.li>
        ))}
      </ol>
    </section>
  );
}

export function PopularCollections({ onCollectionClick }) {
  return (
    <section className="mh-collections" id="collections" data-testid="mh-collections">
      <header className="mh-section-head">
        <div className="mh-ornament-divider">
          <span className="mh-ornament-rule" />
          <span className="mh-ornament-title">EXPLORE POPULAR COLLECTIONS</span>
          <span className="mh-ornament-rule" />
        </div>
        <a href="#all-collections" className="mh-view-all" data-testid="mh-view-all-collections">
          View All
        </a>
      </header>

      <ul className="mh-collection-grid" data-testid="mh-collection-grid">
        {COLLECTIONS.map((c, i) => (
          <motion.li
            key={c.slug}
            className="mh-collection-card"
            initial={{ opacity: 0, y: 22 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-70px" }}
            transition={{ duration: 0.55, delay: i * 0.06, ease: [0.22, 1, 0.36, 1] }}
            data-testid={`mh-collection-${c.slug}`}
            role="button"
            tabIndex={0}
            onClick={() => onCollectionClick && onCollectionClick(c)}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onCollectionClick && onCollectionClick(c);
              }
            }}
          >
            <span className="mh-collection-medallion-wrap">
              <img
                src={ASSET(c.medallion)}
                alt=""
                aria-hidden="true"
                className="mh-collection-medallion"
                loading="lazy"
              />
            </span>
            <h3 className="mh-collection-title">{c.title}</h3>
            <p className="mh-collection-desc">{c.desc}</p>
          </motion.li>
        ))}
      </ul>
    </section>
  );
}

export function TransformationRibbon() {
  return (
    <div
      className="mh-transform-ribbon"
      data-testid="mh-transform-ribbon"
      role="contentinfo"
    >
      <span className="mh-ribbon-om indic" aria-hidden="true">ॐ</span>
      <p className="mh-ribbon-quote">
        “Transformation is not a day’s work, but a lifetime’s art.”
      </p>
      <span className="mh-ribbon-lotus" aria-hidden="true">❀</span>
    </div>
  );
}

// Kept as a backwards-compat alias for older imports.
export const ClosingOmRibbon = TransformationRibbon;
